import { flashcardType, hasAnsweredFlashcard } from '@/utils/flashcards';
import { ANSWER_QUESTIONS_KEY, setChecklistData } from '@/utils/checklist';
import { rateLimitingEnabled } from '@/utils';
import { includedInPaidPurchaseOrders } from '@/utils/acl';

export default {
  data() {
    return {
      answerChanged: false,
      answered: false,
      answerIds: [],
      correct: null,
      rawAnswerData: null,
      sessionPromise: null,
      time: 0,
      timer: null,
    };
  },
  computed: {
    assetAssociationView() {
      // Null check needed for marketing pages
      // TODO: Allow marketing pages to function without this check
      return this.$route && this.$route.name === 'assetAssociation#show';
    },
    currentFlashcard() {
      return (
        this.$store.getters['flashcards/flashcards'][
          this.$route.params.flashcardId
        ] || {}
      );
    },
    hasBeenAnswered() {
      return hasAnsweredFlashcard(this.currentFlashcard, this.answerIds, this.rawAnswerData);
    },
    isTerminology() {
      // Null check needed for marketing pages
      // TODO: Allow marketing pages to function without this check
      if (this.currentFlashcard && this.currentFlashcard.id) {
        return flashcardType(this.currentFlashcard) === 'terminology';
      }
    },
    isQotD() {
      return this.$route.path.includes('/qotd');
    },
    nextFlashcard() {
      if (this.isLearningPath) {
        const learningPath = this.$store.getters['learningPath/flashcards'];
        const currentFlashcardIndex = learningPath.findIndex(item => item.flashcardId === this.currentFlashcard.id);
        return learningPath[currentFlashcardIndex + 1]?.flashcardId || 0;
      }

      const session = this.$store.getters['flashcards/currentSession'];
      return session[session.indexOf(this.currentFlashcard.id) + 1] || 0;
    },
    nextFlashcardGroup() {
      const session = this.$store.getters['flashcards/currentSession'];
      let start = session.indexOf(this.currentFlashcard.id) + 1 || 0;
      let end = start + 5;
      return session.slice(start, end);
    },
    previousFlashcard() {
      if (this.isLearningPath) {
        const learningPath = this.$store.getters['learningPath/flashcards'];
        const currentFlashcardIndex = learningPath.findIndex(item => item.flashcardId === this.currentFlashcard.id);
        return learningPath[currentFlashcardIndex - 1]?.flashcardId || 0;
      }

      const session = this.$store.getters['flashcards/currentSession'];
      return session[session.indexOf(this.currentFlashcard.id) - 1] || 0;
    },
    rateLimitingEnabled() {
      return rateLimitingEnabled({
        store: this.$store,
        apptimize: this.$apptimize,
        analytics: this.$analytics,
      });
    },
  },
  created() {
    if (!this.assetAssociationView) {
      // Null check needed for marketing pages
      // TODO: Allow marketing pages to function without this check
      if (
        this.$route &&
        (this.$route.query.ids || this.$route.query.data) &&
        this.currentFlashcard
      ) {
        this.restoreFlashcardState(this.$route, this.currentFlashcard);
      }
    }
  },
  mounted() {
    if (
      !this.assetAssociationView &&
      this.$route &&
      (this.$route.query.ids || this.$route.query.data)
    ) {
      const { ids, data, ...rest } = this.$route.query;
      this.$router.replace({
        ...this.$route,
        query: rest,
      });
    }
    if (this.isTerminology) {
      this.$analytics.events.viewedTerminology(
        this.currentFlashcard,
        this.currentFlashcardCategory,
      );
    }
  },
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }
  },
  methods: {
    answerFlashcard() {
      if (this.timer) {
        clearInterval(this.timer);
      }

      // Load next flashcard
      if (
        this.nextFlashcard &&
        !this.$store.getters['flashcards/flashcards'][this.nextFlashcard]
      ) {
        let params = {
          ids: this.nextFlashcardGroup,
          rateLimited: this.rateLimitingEnabled,
        };
        this.$store.dispatch('flashcards/fetchFlashcards', params);
      }

      // Evaluate the answer
      const correct = this.evaluateAnswer(
        this.currentFlashcard,
        this.answerIds,
        this.rawAnswerData,
      );
      this.correct = correct;

      this.$analytics.events.answeredFlashcard(
        this.$store,
        this.currentFlashcardCategory,
        this.currentFlashcard,
        this.answerIds,
        this.rawAnswerData,
        correct,
        this.isQotD,
      );
      this.answered = true;
      this.scrollToTop();
    },
    createFlashcardState(params = {}) {
      const unansweredStatus = this.$store.getters[
        'flashcardStatuses/unansweredFlashcardStatus'
      ];
      const greenFlashcardStatus = this.$store.getters[
        'flashcardStatuses/greenFlashcardStatus'
      ];
      const redFlashcardStatus = this.$store.getters[
        'flashcardStatuses/redFlashcardStatus'
      ];

      const correct = this.evaluateAnswer(
          this.currentFlashcard,
          this.answerIds,
          this.rawAnswerData,
        );
      const defaults = {
        answerIds: this.answerIds,
        rawAnswerData: this.rawAnswerData,
        categoryId: (this.currentFlashcardCategory || {}).id,
        correct: correct,
        flashcardId: this.currentFlashcard.id,
        flashcardStatusId: unansweredStatus.id,
        lessonId: params.lessonId,
        mobileCreatedAt: Date.now(),
        rateLimited: this.rateLimitingEnabled,
        timeInSeconds: 0,
        userCertificationId: this.$route.params.userCertificationId,
      };

      const mergedParams = { ...defaults, ...params };
      const existingFlashcardStates = this.$store.getters['flashcardStates/flashcardStates'] || {};
      const userQuizzes = this.$store.getters['userQuizzes/all'] || [];

      const flashcardId = mergedParams.flashcardId;
      // Do have a quiz with this flashcard?
      const matchingQuiz = userQuizzes.find(quiz => quiz.flashcardCategoryIds && quiz.flashcardCategoryIds.some(fc => fc.flashcardId === flashcardId));
      let flashcardStateExists = false;
      let categoryId = null;
      if (matchingQuiz) {
        // Get the categoryId from the matching userQuiz object
        const matchingFlashcard = matchingQuiz.flashcardCategoryIds.find(fc => fc.flashcardId === flashcardId);
        categoryId = matchingFlashcard ? matchingFlashcard.categoryId : null;
        const flashcardStateKey = `${flashcardId}-category-${categoryId}`;
        // Have we already created a state for this flashcard and category ID?
        flashcardStateExists = existingFlashcardStates.hasOwnProperty(flashcardStateKey);
      }

      this.$store.dispatch('flashcardStates/send', mergedParams);
      if (!flashcardStateExists && categoryId !== null) {
        let flashcardStatus = redFlashcardStatus;
        if (correct) {
          flashcardStatus = greenFlashcardStatus;
        }
        const newFlashcardState = {
          ...mergedParams,
          ...{flashcardStatusId: flashcardStatus.id},
          categoryId,
        };
        this.$store.dispatch('flashcardStates/send', newFlashcardState);
      }
      if (mergedParams.flashcardStatusId !== unansweredStatus.id && mergedParams.lessonId === undefined) {
        this.incrementDailyQuestions();
      }

      if (mergedParams.lessonId && this.checkLessonCompletion) {
        this.checkLessonCompletion(mergedParams.lessonId);
      }
    },
    decrementRateLimitCredits(flashcard, category) {
      const hasPurchasedAccess = flashcard?.categoryIds.some(cid => {
        const cat = this.$store.getters['categories/categories'][cid];
        if (!cat) return false;
        return includedInPaidPurchaseOrders(this.$store, cat);
      });

      if (this.rateLimitingEnabled && !hasPurchasedAccess) {
        this.$store.dispatch('user/spendCredit').then((data) => {
          if (data?.remainingCredits === 0) {
            this.$modal.show('rate-limit-reached', {
              category,
              flashcard,
            });
          }
        });
      }
    },
    evaluateAnswer(flashcard, flashcardAnswerIds, rawAnswerData) {
      if (
        flashcard.type === 'Multiple Choice' ||
        flashcard.type === 'Bowtie' ||
        flashcard.type === 'Blank Text Completion' ||
        flashcard.type === 'Single Correct SATA' ||
        flashcard.type === 'SATA' ||
        flashcard.type === 'Cloze / Dropdown' ||
        flashcard.type === 'Drag and Drop' ||
        flashcard.type === 'Highlight'
      ) {
        const a = flashcardAnswerIds.sort();
        const b = flashcard.answers
          .filter((answer) => answer.correct)
          .map((answer) => answer.id)
          .sort();
        return a.length == b.length &&
          !a.map((id, index) => id == b[index]).includes(false);
      } else if (flashcard.type == 'Ordered Response') {
        const orderedAnswerIds = (rawAnswerData || {}).orderedAnswerIds || [];

        // invalid state
        if (orderedAnswerIds.length < 1) return false;

        // sort the flashcard answers correctly
        const orderedAnswers = flashcard.answers.sort((a, b) => {
          return a.correctOrder - b.correctOrder;
        });

        let correctOrder = true;

        orderedAnswers.forEach((answer, index) => {
          // return false if any answer isn't in correct order
          if (answer.id !== orderedAnswerIds[index]) {
            correctOrder = false;
          }
        });

        return correctOrder;
      } else if (flashcard.type === 'Hotspot') {
        let x = rawAnswerData.x;
        let y = rawAnswerData.y;
        let hotspot = flashcard.hotspots[0];

        return (
          x >= hotspot.x1 &&
          x <= hotspot.x2 &&
          y >= hotspot.y1 &&
          y <= hotspot.y2
        );
      } else if (flashcard.type === 'Numeric Entry') {
        const answer = JSON.parse(flashcard.answers[0].text).numeric;
        return parseFloat(rawAnswerData.numeric) == parseFloat(answer);
      } else if (flashcard.type === 'Fractional Numeric Entry') {
        const correctNumerator = parseFloat(
          JSON.parse(flashcard.answers[0].text).numerator,
        );
        const correctDenominator = parseFloat(
          JSON.parse(flashcard.answers[0].text).denominator,
        );
        return (
          correctNumerator / correctDenominator ===
          parseFloat(this.rawAnswerData.numerator) /
            parseFloat(this.rawAnswerData.denominator)
        );
      } else if (flashcard.type === 'Matrix Grid') {
        // First we need to determine if the correct number of answers are present in the rawAnswerData array
        // And that the answers in said array match the current answers on the flashcard
        // If the lengths don't match, it can't be correct.
        const answerIds = flashcard.answers.map((answer) => answer.id);
        const currentMatrixGridSelections = (
          rawAnswerData.matrixGridSelections || []
        ).filter((selection) =>
          answerIds.includes(selection.answerId),
        );
        if (answerIds.length !== currentMatrixGridSelections.length) {
          console.log(
            'Matrix Grid evaluation error: lengths of answers did not match flashcard',
          );
          return false;
        }

        // Next, we need to go though each answer in the flashcard answers array and check the matrixGridCorrect
        // property again the selections property of the corresponding object in the currentMatrixGridSelections
        // Order matters here so we need to validate that the correct number of true and false values are present
        // and we need make sure that at each index, the values are the same
        return flashcard.answers.every((flashcardAnswer) => {
          const userAnswer = currentMatrixGridSelections.find(
            (selection) => selection.answerId === flashcardAnswer.id,
          );
          if (
            flashcardAnswer.matrixGridCorrect.length !==
            userAnswer.selections.length
          ) {
            console.log(
              'Matrix Grid evaluation error: lengths of selections for flashcard answers did not match',
            );
            console.log('flashcardAnswer', flashcardAnswer.id);
            return false;
          }

          return flashcardAnswer.matrixGridCorrect.every(
            (answer, index) => userAnswer.selections[index] === answer,
          );
        });
      } else {
        return false;
      }
    },
    goToNextFlashcard() {
      this.resetFlashcard();
      this.$router.push(this.nextFlashcardRoute);
    },
    goToPreviousFlashcard() {
      this.resetFlashcard();
      this.$router.push(this.previousFlashcardRoute);
    },
    incrementDailyQuestions() {
      this.$store
        .dispatch('flashcardStates/incrementDailyQuestions')
        .then((totalAnswered) => {
          const studyGoal = this.$store.getters['session/currentUser']
            .userProfile?.studyGoal;
          if (totalAnswered === studyGoal) {
            this.$analytics.events.completedDailyGoal();
            this.$modal.show('study-goal-success');
          }
        });
    },
    resetFlashcard(id) {
      this.answered = false;
      this.answerIds = [];
      this.rawAnswerData = null;
      this.correct = null;
      if (id) {
        this.time = 0;
        const context = this;
        this.timer = setInterval(function () {
          context.time++;
        }, 1000);
      }
    },
    resetFlashcardFromState(id) {
      if (!id || !this.currentFlashcardCategory) {
        this.resetFlashcard(id);
        return;
      }

      const flashcardStateKey = `${id}-category-${this.currentFlashcardCategory.id}`;
      const allFlashcardStates = this.$store.getters['flashcardStates/flashcardStates'];
      const flashcardState = allFlashcardStates[flashcardStateKey];

      if (flashcardState) {
        this.answered = false;
        this.answerIds = flashcardState.answerIds;
        this.rawAnswerData = JSON.parse(JSON.stringify(flashcardState.rawAnswerData || null));
        this.correct = flashcardState.correct;
        this.answerChanged = false;
      } else {
        this.resetFlashcard(id);
        this.answerChanged = false;
      }
    },
    restoreFlashcardState(route, flashcard) {
      const answerIds = route.query.ids;
      const answerData = route.query.data;

      // Convert string IDs back to numbers if they came from query params,
      // and ensure we always pass an array to setAnswerIds.
      const numericAnswerIds = this.normalizeAnswerIds(answerIds);

      this.setAnswerIds(numericAnswerIds);
      this.setRawAnswerData(answerData);
      this.correct = this.evaluateAnswer(flashcard, numericAnswerIds, answerData);
      this.answered = true;
    },
    normalizeAnswerIds(answerIds) {
      // Return empty array if no answer IDs provided
      if (!answerIds) {
        return [];
      }

      // Convert single value to array
      const idsArray = Array.isArray(answerIds) ? answerIds : [answerIds];

      // Convert all string IDs to numbers
      return idsArray.map(id => (typeof id === 'string' ? parseInt(id, 10) : id));
    },
    scrollToTop() {
      if (this.$refs?.['flashcard-view-container']) {
        this.$refs['flashcard-view-container'].scroll({
          top: 0,
          behavior: 'smooth',
        });
      } else {
        window.scroll({
          top: 0,
          behavior: 'smooth',
        });
      }
    },
    setAnswerIds(ids) {
      this.answerChanged = true;
      this.answerIds = [...ids];
    },
    setRawAnswerData(answerData) {
      this.answerChanged = true;
      this.rawAnswerData = answerData;
    },
    skipFlashcard() {
      this.$analytics.events.skippedFlashcard(
        this.currentFlashcardCategory,
        this.currentFlashcard,
        this.isTerminology,
      );
      this.goToNextFlashcard();
      this.$store.commit('ui/setShowAssetTray', false);
    },
    tagFlashcard(flashcardStatusId, lessonId = null) {
      const status = this.$store.getters[
        'flashcardStatuses/flashcardStatuses'
      ].find((fs) => fs.id == flashcardStatusId).name;
      if (this.isTerminology) {
        this.$analytics.events.viewedTerminologyRationale(
          this.currentFlashcard,
          this.currentFlashcardCategory,
          status,
        );
      } else {
        this.$analytics.events.taggedFlashcard(
          this.$store,
          status,
          this.currentFlashcardCategory,
          this.currentFlashcard,
          this.answerIds,
          this.correct,
        );
      }

      // for lessons we need 2 flashcard states, one for the category and one for the lesson
      if (lessonId) {
        this.createFlashcardState({
          flashcardStatusId: flashcardStatusId,
          timeInSeconds: this.time,
          lessonId: lessonId,
          categoryId: null,
        });
      }

      this.createFlashcardState({
        flashcardStatusId: flashcardStatusId,
        timeInSeconds: this.time,
      });

      // using flashcard states here since checklist users should be new, and shouldn't need to worry about reset states yet
      setChecklistData(
        this.$store,
        ANSWER_QUESTIONS_KEY,
        this.$notify,
        this.$store.getters['flashcardStates/flashcardStateList'].length,
      );

      if (!this.isQotD) {
        this.decrementRateLimitCredits(this.currentFlashcard, this.currentFlashcardCategory);
      }
      this.goToNextFlashcard();
      this.scrollToTop();
      this.$store.commit('ui/setShowAssetTray', false);
    },
  },
};
